import { SplitTypes } from 'domains/campaigns/types';

type SplittableSend = {
    id?: number;
    parentId?: number;
    splitType?: SplitTypes;
    splitNodeId?: string;
};

/**
 * Published / completed campaigns reconstruct their send topology from `campaign.structure`,
 * which records `parentId` + `splitType` but NOT `splitNodeId` — a frontend-only editing aid
 * that is stripped before publish. Nested-split detection in `navTree.buildNavForest` and
 * `buildCampaignGraph` keys off `splitNodeId`, so without it an inner conditional split
 * collapses into a flat chain of sends (no split box, and the inner DOES_NOT_MEET branch root
 * is mistaken for a chained send, gaining a spurious time-delay button).
 *
 * This rebuilds a stable `splitNodeId` purely from the structure: a parent that has BOTH a
 * MEETS child and a DOES_NOT_MEET child opens a split, and both branch-root children share
 * `splitNodeId = split-<parentId>`. This mirrors the draft semantics exactly — both branches
 * of one split share an id, and a nested branch root's id differs from its parent branch's id,
 * which is precisely the signal the detection logic relies on.
 *
 * Draft sends already carry `splitNodeId`, so this is a no-op for them (it bails the moment any
 * send already has one) and only fills the gap for published / completed campaigns.
 */
const synthesizeSplitNodeIds = <T extends SplittableSend>(sends: T[]): T[] => {
    if (!sends?.length) return sends;
    // Draft flow already carries splitNodeId — leave it untouched.
    if (sends.some((s) => s.splitNodeId != null)) return sends;

    const childrenByParent = new Map<number, T[]>();
    sends.forEach((send) => {
        if (send.parentId == null) return;
        const group = childrenByParent.get(send.parentId) ?? [];
        group.push(send);
        childrenByParent.set(send.parentId, group);
    });

    const splitNodeIdByChildId = new Map<number, string>();
    // Exit sends have no id, so we track them by their index in the array.
    const splitNodeIdByIndex = new Map<number, string>();
    childrenByParent.forEach((children, parentId) => {
        const hasMeets = children.some((c) => c.splitType === SplitTypes.MEETS);
        const hasDoesNotMeet = children.some((c) => c.splitType === SplitTypes.DOES_NOT_MEET);
        if (!hasMeets || !hasDoesNotMeet) return;

        const splitNodeId = `split-${parentId}`;
        children.forEach((child) => {
            const isBranchRoot = child.splitType === SplitTypes.MEETS || child.splitType === SplitTypes.DOES_NOT_MEET;
            if (!isBranchRoot) return;
            if (child.id != null) {
                splitNodeIdByChildId.set(child.id, splitNodeId);
            } else {
                // Exit sends (no DB row → no id) are branch roots too. Track by array index so the
                // final map step can assign their splitNodeId without keying off id.
                const idx = sends.indexOf(child);
                if (idx !== -1) splitNodeIdByIndex.set(idx, splitNodeId);
            }
        });
    });

    if (splitNodeIdByChildId.size === 0 && splitNodeIdByIndex.size === 0) return sends;

    return sends.map((send, idx) => {
        if (send.id != null && splitNodeIdByChildId.has(send.id)) {
            return { ...send, splitNodeId: splitNodeIdByChildId.get(send.id) };
        }
        if (send.id == null && splitNodeIdByIndex.has(idx)) {
            return { ...send, splitNodeId: splitNodeIdByIndex.get(idx) };
        }
        return send;
    });
};

export default synthesizeSplitNodeIds;
